import { Avatar, Badge, Button, MultiSelect, type SelectOption } from '@bs/ui-components'
import { Bookmark, Copy, ExternalLink, MapPin, MoreHorizontal, Search, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { sanitizeJobDescription } from '@/utils/sanitize-html'
import { companyLogoUrl } from '@/services/assets'
import { SearchPicker, type SearchPickerOption } from '@/components/ui/search-picker'
import { formatLocation, getCities, getCompanies, getIndustries, getLookup, getSkills, labelsFor, type CityItem, type CompanyItem, type MetadataItem } from '@/services/metadata'
import {
  applyToJob,
  getApplications,
  getJobBySlug,
  getSavedJobs,
  saveJob,
  searchJobs,
  type Application,
  type JobSearchDetail,
  type JobSearchItem,
  type SavedJob,
} from '@/features/jobs/api'

type FilterKey = 'cities' | 'jobTypes' | 'industries' | 'degreeTypes' | 'degreeFields' | 'seniority'

/** What one row in the results list needs — API items are adapted into this ONCE, at the top, so
 * the list/row rendering below never touches the raw search-item shape. */
interface JobRow {
  key: string
  slug: string
  title: string
  company: string
  companyLogoUrl?: string
  location: string
  badges: string[]
}

export function JobSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('term') ?? '')
  const [currentSlug, setCurrentSlug] = useState('')
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false)
  const sort = searchParams.get('sort') === 'date' ? 'date' : 'relevance'
  const filters = useMemo(() => readFilters(searchParams), [searchParams])
  const term = searchParams.get('term') ?? ''

  const [realJobs, setRealJobs] = useState<JobSearchItem[]>([])
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [workspaceTypes, setWorkspaceTypes] = useState<MetadataItem[]>([])
  const [seniorityLookup, setSeniorityLookup] = useState<MetadataItem[]>([])
  const [positionTypeLookup, setPositionTypeLookup] = useState<MetadataItem[]>([])
  const [industryLookup, setIndustryLookup] = useState<MetadataItem[]>([])
  const [degreeTypeLookup, setDegreeTypeLookup] = useState<MetadataItem[]>([])
  const [degreeNameLookup, setDegreeNameLookup] = useState<MetadataItem[]>([])
  const [skillLookup, setSkillLookup] = useState<MetadataItem[]>([])
  const [companiesById, setCompaniesById] = useState<Record<string, CompanyItem>>({})
  const [citiesById, setCitiesById] = useState<Record<number, CityItem>>({})
  const [currentDetail, setCurrentDetail] = useState<JobSearchDetail | null>(null)
  const [filtersError, setFiltersError] = useState('')
  const [searchError, setSearchError] = useState('')

  // The filter vocabulary itself — fetched once, independent of any search term or selected
  // filter, unlike the job fetch below which re-runs on every one of those.
  useEffect(() => {
    let cancelled = false
    Promise.all([
      getLookup('workspaceTypes'),
      getLookup('seniorityLevels'),
      getLookup('positionTypes'),
      getIndustries(),
      getLookup('degreeTypes'),
      getLookup('degreeNames'),
      getSkills(),
    ])
      .then(([workspaceType, seniority, positionType, industry, degreeType, degreeName, skills]) => {
        if (cancelled) return
        setWorkspaceTypes(workspaceType)
        setSeniorityLookup(seniority)
        setPositionTypeLookup(positionType)
        setIndustryLookup(industry)
        setDegreeTypeLookup(degreeType)
        setDegreeNameLookup(degreeName)
        setSkillLookup(skills)
      })
      .catch(() => {
        // Surfaced, not swallowed — without the vocabulary every filter dropdown opens empty,
        // which reads as "no filters exist" rather than as a failed load.
        if (!cancelled) setFiltersError('Could not load the filter options — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // A `term` is required — this is a SEARCH page, not a browse-everything one, and the
    // unfiltered "just show recent jobs" list was almost entirely jobs with no real company to
    // link to (the newest scraped batch), which read as broken rather than as a browse mode.
    // Empty, not an error: no API call, no message, just nothing to show yet.
    if (!term.trim()) {
      setRealJobs([])
      setSearchError('')
      return
    }
    let cancelled = false
    setSearchError('')
    Promise.all([
      searchJobs({
        term,
        cities: toNumberList(filters.cities),
        positionTypes: toNumberList(filters.jobTypes),
        industries: toNumberList(filters.industries),
        degreeTypes: toNumberList(filters.degreeTypes),
        degreeNames: toNumberList(filters.degreeFields),
        seniority: toNumberList(filters.seniority),
        sort,
        limit: 50,
      }),
      getSavedJobs().catch(() => ({ items: [] as SavedJob[] })),
      getApplications().catch(() => ({ items: [] as Application[] })),
    ])
      .then(async ([jobsRes, savedRes, appsRes]) => {
        if (cancelled) return
        const companyIds = Array.from(new Set(jobsRes.items.map((j) => j.companyId)))
        const cityIds = Array.from(
          new Set(jobsRes.items.flatMap((j) => (j.location?.cityId !== undefined ? [j.location.cityId] : []))),
        )
        const [companiesRes, citiesRes] = await Promise.all([
          companyIds.length ? getCompanies({ ids: companyIds }) : Promise.resolve([]),
          cityIds.length ? getCities({ ids: cityIds }) : Promise.resolve([]),
        ])
        if (cancelled) return
        setRealJobs(jobsRes.items)
        setSavedJobs(savedRes.items)
        setApplications(appsRes.items)
        setCompaniesById((current) => ({ ...current, ...Object.fromEntries(companiesRes.map((c) => [c._id, c])) }))
        // Merged, not replaced: a city (or company) a later page hides is still resolvable — the
        // Location field and `options.cities` below both read off this map, not off the latest
        // fetch alone, so picking a city with zero current results doesn't un-name itself.
        setCitiesById((current) => ({ ...current, ...Object.fromEntries(citiesRes.map((c) => [c.cityId, c])) }))
      })
      .catch(() => {
        // Surfaced, not swallowed — a failed search rendering "No matching jobs" is
        // indistinguishable from a search that genuinely matched nothing.
        if (!cancelled) setSearchError('The search failed — try again in a moment.')
      })
    return () => {
      cancelled = true
    }
  }, [term, filters, sort])

  // Lazy detail fetch for whichever job is currently previewed — `LIST_FIELDS` excludes
  // `description`/`industries`/`skills` (tens of KB of HTML per job, per job-search.service.ts),
  // so the preview pane fills those in only once a job is actually selected.
  //
  // Guarded on `realJobs` actually HAVING `currentSlug`: a stale slug from a previous search must
  // not fire `getJobBySlug` against the live API and 404.
  useEffect(() => {
    if (!currentSlug || !realJobs.some((job) => job.slug === currentSlug)) return
    let cancelled = false
    setCurrentDetail(null)
    getJobBySlug(currentSlug)
      .then((job) => {
        if (!cancelled) setCurrentDetail(job)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [currentSlug, realJobs])

  /**
   * Real mode's option VALUES are elIds (as strings) — what the ES filter on `bs_jobs` actually
   * takes — not the display label; `MultiSelect` renders `label` and toggles by `value`. Cities
   * have no "browse all 722" mode anywhere else in the app either (see `getCities`'s own
   * comment) — so the panel can only ever offer cities that have shown up in a fetched page or
   * been picked via the Location field, same honesty tradeoff a hardcoded empty array would make,
   * minus the "always empty" part.
   */
  const options: Record<FilterKey, SelectOption[]> = useMemo(() => ({
    cities: Object.values(citiesById)
      .map((c) => ({ value: String(c.cityId), label: formatLocation(c, c.country) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    jobTypes: toOptions(positionTypeLookup),
    industries: toOptions(industryLookup),
    degreeTypes: toOptions(degreeTypeLookup),
    degreeFields: toOptions(degreeNameLookup),
    seniority: toOptions(seniorityLookup),
  }), [citiesById, positionTypeLookup, industryLookup, degreeTypeLookup, degreeNameLookup, seniorityLookup])

  const results: JobRow[] = useMemo(
    () => realJobs.map((item) => toJobRow(item, { companiesById, citiesById, workspaceTypes })),
    [realJobs, companiesById, citiesById, workspaceTypes],
  )
  const currentRealItem = realJobs.find((item) => item.slug === currentSlug) ?? realJobs[0]
  const currentCompany = currentRealItem ? resolveCompany(currentRealItem, companiesById) : undefined
  const currentCity = currentRealItem?.location?.cityId !== undefined ? citiesById[currentRealItem.location.cityId] : undefined

  useEffect(() => {
    if (results.length && !results.some((row) => row.slug === currentSlug)) setCurrentSlug(results[0]!.slug)
  }, [currentSlug, results])

  function snapshotFor(job: JobSearchItem) {
    const company = companiesById[job.companyId]
    return {
      jobId: job._id,
      companyId: job.companyId,
      companyData: { ...(company?.name && { name: company.name }), ...(company?.logoUrl && { logoUrl: company.logoUrl }) },
      jobData: {
        title: job.title,
        slug: job.slug,
        ...(job.location?.cityId !== undefined && { cityId: job.location.cityId }),
        ...(job.location?.country && { country: job.location.country }),
      },
    }
  }

  async function handleApply(jobId: string) {
    const job = realJobs.find((item) => item._id === jobId)
    if (!job || applications.some((a) => a.jobId === jobId && a.status !== 'withdrawn')) return
    const { application } = await applyToJob(snapshotFor(job)).catch(() => ({ application: undefined }))
    if (application) setApplications((current) => [application, ...current])
  }

  async function handleSave(jobId: string) {
    const job = realJobs.find((item) => item._id === jobId)
    if (!job) return
    const already = savedJobs.some((s) => s.jobId === jobId)
    if (already) return // unsaving from this page's preview isn't wired — Saved tab on /jobs handles removal
    const { savedJob } = await saveJob(snapshotFor(job)).catch(() => ({ savedJob: undefined }))
    if (savedJob) setSavedJobs((current) => [savedJob, ...current])
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const next = new URLSearchParams(searchParams)
    if (query.trim()) next.set('term', query.trim())
    else next.delete('term')
    setSearchParams(next)
  }

  function setFilterValues(key: FilterKey, values: string[]) {
    const next = new URLSearchParams(searchParams)
    if (values.length) next.set(key, values.join('|'))
    else next.delete(key)
    setSearchParams(next)
  }

  /** The Location field is a second entry point onto the SAME `cities` filter `setFilterValues`
   * writes — picking here replaces it with one city; the "Filter Cities" dropdown can still add
   * more on top. Two affordances, one URL param, so they can never drift apart. */
  function pickLocation(option: SearchPickerOption) {
    const city = option.data as CityItem
    setCitiesById((current) => ({ ...current, [city.cityId]: city }))
    setFilterValues('cities', [option.id])
  }

  function setSort(value: 'relevance' | 'date') {
    const next = new URLSearchParams(searchParams)
    if (value === 'date') next.set('sort', 'date')
    else next.delete('sort')
    setSearchParams(next)
  }

  /** Clears the filter dropdowns only — `term` stays. Search is required to show anything at all
   * here, so wiping it alongside the filters would just swap "no results" for "no results",
   * with no way back to a result set short of retyping the search. */
  function clearFilters() {
    const next = new URLSearchParams(searchParams)
    for (const key of ['cities', 'jobTypes', 'industries', 'degreeTypes', 'degreeFields', 'seniority'] as const) next.delete(key)
    setSearchParams(next)
  }

  const hasActiveFilter = Boolean(filters.cities.length || filters.jobTypes.length || filters.industries.length || filters.degreeTypes.length || filters.degreeFields.length || filters.seniority.length)
  const locationCityId = filters.cities.length === 1 ? Number(filters.cities[0]) : undefined
  const locationCity = locationCityId !== undefined ? citiesById[locationCityId] : undefined
  const locationLabel = locationCity ? formatLocation(locationCity, locationCity.country) : ''
  // So job-detail-page.tsx's "Back to jobs" returns to this exact search/filter state instead of
  // a bare `/jobs` that has no idea a search even happened.
  const backTo = `/job-search${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  return (
    <main className="mx-auto max-w-[1460px] px-4 py-10 sm:px-6 lg:px-8">
      <section className="rounded-[20px] bg-primary px-5 py-8 text-white shadow-sm sm:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/65">Find your next role</p>
        <h1 className="mt-2 text-3xl font-semibold">Search open jobs</h1>
        <form onSubmit={submit} className="mt-6 grid gap-3 rounded-2xl bg-white p-3 sm:grid-cols-[1fr_1fr_auto]">
          <label className="relative"><Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Job title, skill or company" className="h-12 w-full rounded-xl border border-[#b8bcc8] bg-white pl-12 pr-4 text-sm text-[#404040] outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" /></label>
          <SearchPicker
            multiple={false}
            values={locationCity ? [locationLabel] : []}
            onChange={(next) => { if (!next.length) setFilterValues('cities', []) }}
            onSearch={(q) => getCities({ q }).then(citiesToOptions)}
            onPick={pickLocation}
            placeholder="Location"
            className="flex h-12 items-center border-[#b8bcc8] bg-white p-0"
          />
          <Button type="submit" size="lg" className="h-12">Search jobs</Button>
        </form>
      </section>

      <div className="mt-5 flex flex-wrap items-center gap-3 rounded-[20px] border border-border bg-white p-4">
        <MultiSelect ariaLabel="Filter by city" values={filters.cities} options={options.cities} onValuesChange={(next) => setFilterValues('cities', next)} placeholder="Filter Cities" className="min-w-[170px] flex-1" />
        <MultiSelect ariaLabel="Filter by position type" values={filters.jobTypes} options={options.jobTypes} onValuesChange={(next) => setFilterValues('jobTypes', next)} placeholder="Position type" className="min-w-[170px] flex-1" />
        <MultiSelect ariaLabel="Filter by industry" values={filters.industries} options={options.industries} onValuesChange={(next) => setFilterValues('industries', next)} placeholder="Filter Industries" className="min-w-[170px] flex-1" />
        <MultiSelect ariaLabel="Filter by education degree" values={filters.degreeTypes} options={options.degreeTypes} onValuesChange={(next) => setFilterValues('degreeTypes', next)} placeholder="Education Degree" className="min-w-[170px] flex-1" />
        <MultiSelect ariaLabel="Filter by education field" values={filters.degreeFields} options={options.degreeFields} onValuesChange={(next) => setFilterValues('degreeFields', next)} placeholder="Education Field" className="min-w-[170px] flex-1" />
        {/* Legacy FiltersPanel "Seniority" — the one filter the port had dropped. */}
        <MultiSelect ariaLabel="Filter by seniority" values={filters.seniority} options={options.seniority} onValuesChange={(next) => setFilterValues('seniority', next)} placeholder="Seniority" className="min-w-[150px] flex-1" />
        {hasActiveFilter ? <Button variant="ghost" size="sm" onClick={clearFilters}><X className="size-4" /> Clear</Button> : null}
        {filtersError ? <p role="alert" className="w-full text-sm text-destructive">{filtersError}</p> : null}
      </div>

      <div className="mt-5">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-2xl font-semibold text-primary">Job Search ({results.length})</h2><p className="mt-1 text-sm text-muted-foreground">Select a job to preview its details.</p></div><label className="flex items-center gap-2 text-sm text-[#555]">Sort by<select value={sort} onChange={(event) => setSort(event.target.value as 'relevance' | 'date')} className="h-10 rounded-xl border border-input-border bg-white px-3 outline-none focus:border-accent"><option value="relevance">Relevance</option><option value="date">Date</option></select></label></div>
        {results.length ? <div className="grid overflow-hidden rounded-[20px] border border-border bg-white shadow-sm lg:h-[720px] lg:grid-cols-[330px_1fr]">
          <div className="max-h-[720px] overflow-y-auto border-b border-border lg:h-full lg:max-h-none lg:border-b-0 lg:border-r">{results.map((row) => <SearchResultItem key={row.key} row={row} active={row.slug === currentSlug} onClick={() => { setCurrentSlug(row.slug); setMobilePreviewOpen(true) }} />)}</div>
          <div className="hidden lg:block lg:h-full lg:overflow-y-auto">
            {currentRealItem ? (
                  <RealJobPreview
                    item={currentRealItem}
                    detail={currentDetail}
                    company={currentCompany}
                    city={currentCity}
                    backTo={backTo}
                    workspaceLabel={workspaceTypes.find((w) => w.elId === currentRealItem.workspaceType)?.value ?? ''}
                    seniorityLabels={labelsFor(seniorityLookup, currentRealItem.seniorityLevel)}
                    positionTypeLabels={labelsFor(positionTypeLookup, currentRealItem.positionType)}
                    industryLabels={labelsFor(industryLookup, currentDetail?.industries)}
                    skillLabels={labelsFor(skillLookup, currentDetail?.mandatorySkills?.length ? currentDetail.mandatorySkills : currentDetail?.skills)}
                    saved={savedJobs.some((s) => s.jobId === currentRealItem._id)}
                    applied={applications.some((a) => a.jobId === currentRealItem._id && a.status !== 'withdrawn')}
                    onApply={() => void handleApply(currentRealItem._id)}
                    onSave={() => void handleSave(currentRealItem._id)}
                  />
                ) : null}
          </div>
        </div> : (!term.trim() ? (
          <div className="rounded-[20px] border border-dashed border-[#b8bcc8] bg-white px-6 py-16 text-center"><h3 className="text-lg font-semibold text-primary">Search for a job</h3><p className="mt-2 text-sm text-muted-foreground">Type a job title, skill or company above to see results.</p></div>
        ) : searchError ? (
          // A failed search, not an empty one — "No matching jobs" here would be a lie.
          <div role="alert" className="rounded-[20px] border border-dashed border-destructive/40 bg-white px-6 py-16 text-center"><h3 className="text-lg font-semibold text-destructive">Something went wrong</h3><p className="mt-2 text-sm text-muted-foreground">{searchError}</p></div>
        ) : (
          <div className="rounded-[20px] border border-dashed border-[#b8bcc8] bg-white px-6 py-16 text-center"><h3 className="text-lg font-semibold text-primary">No matching jobs</h3><p className="mt-2 text-sm text-muted-foreground">Try a broader keyword or remove some filters.</p>{hasActiveFilter ? <Button variant="outline" className="mt-5" onClick={clearFilters}>Clear filters</Button> : null}</div>
        ))}
      </div>
      {mobilePreviewOpen ? <div className="fixed inset-0 z-[100] overflow-y-auto bg-primary/45 p-4 backdrop-blur-[2px] lg:hidden" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setMobilePreviewOpen(false) }}>
        <div role="dialog" aria-modal="true" aria-label="Job preview" className="mx-auto my-6 max-w-2xl rounded-[20px] bg-white shadow-xl">
          <div className="flex justify-end border-b border-border p-3"><Button type="button" size="icon" variant="ghost" onClick={() => setMobilePreviewOpen(false)} aria-label="Close job preview"><X className="size-5" /></Button></div>
          {currentRealItem ? (
                <RealJobPreview
                  item={currentRealItem}
                  detail={currentDetail}
                  company={currentCompany}
                  city={currentCity}
                  backTo={backTo}
                  workspaceLabel={workspaceTypes.find((w) => w.elId === currentRealItem.workspaceType)?.value ?? ''}
                  seniorityLabels={labelsFor(seniorityLookup, currentRealItem.seniorityLevel)}
                  positionTypeLabels={labelsFor(positionTypeLookup, currentRealItem.positionType)}
                  industryLabels={labelsFor(industryLookup, currentDetail?.industries)}
                  skillLabels={labelsFor(skillLookup, currentDetail?.mandatorySkills?.length ? currentDetail.mandatorySkills : currentDetail?.skills)}
                  saved={savedJobs.some((s) => s.jobId === currentRealItem._id)}
                  applied={applications.some((a) => a.jobId === currentRealItem._id && a.status !== 'withdrawn')}
                  onApply={() => void handleApply(currentRealItem._id)}
                  onSave={() => void handleSave(currentRealItem._id)}
                />
              ) : null}
        </div>
      </div> : null}
    </main>
  )
}

function SearchResultItem({ row, active, onClick }: { row: JobRow; active: boolean; onClick(): void }) {
  return (
    <button type="button" onClick={onClick} className={`block w-full border-b border-border p-5 text-left transition last:border-b-0 ${active ? 'bg-accent/[0.07]' : 'hover:bg-input'}`}>
      <div className="flex items-start gap-3">
        <Avatar {...(row.companyLogoUrl ? { src: row.companyLogoUrl } : {})} name={row.company || row.title} className="size-11 shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-primary">{row.title}</h3>
          <p className="mt-1 text-sm text-[#555]">{row.company}</p>
          {row.location ? <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="size-3" /> {row.location}</p> : null}
          {row.badges.length ? <div className="mt-3 flex flex-wrap gap-1.5">{row.badges.map((badge) => <Badge key={badge} variant="muted">{badge}</Badge>)}</div> : null}
        </div>
      </div>
    </button>
  )
}

/** The selected job's preview pane (Apply/Save, description) — reads straight off the real search
 * types, since they carry fields (seniority, industries, skills, a company slug to link to) the
 * row shape has no room for. */
function RealJobPreview({
  item,
  detail,
  company,
  city,
  backTo,
  workspaceLabel,
  seniorityLabels,
  positionTypeLabels,
  industryLabels,
  skillLabels,
  saved,
  applied,
  onApply,
  onSave,
}: {
  item: JobSearchItem
  detail: JobSearchDetail | null
  company: CompanyView | undefined
  city: CityItem | undefined
  /** Where job-detail-page.tsx's "Back to jobs" link should return to — this exact search, not a bare `/jobs`. */
  backTo: string
  workspaceLabel: string
  seniorityLabels: string[]
  positionTypeLabels: string[]
  industryLabels: string[]
  skillLabels: string[]
  saved: boolean
  applied: boolean
  onApply(): void
  onSave(): void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function closeMenu(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('pointerdown', closeMenu)
    return () => document.removeEventListener('pointerdown', closeMenu)
  }, [menuOpen])

  const locationText = formatLocation(city, item.location?.country)
  const typeLabel = positionTypeLabels.join(', ') || workspaceLabel

  return (
    <div className="min-w-0 p-6 sm:p-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex gap-4">
            <Avatar {...(company?.logoUrl ? { src: company.logoUrl } : {})} name={company?.name ?? item.title} className="size-14 shrink-0 text-lg" />
            <div className="min-w-0">
              <Link to={`/job/${item.slug}`} state={{ from: backTo }} className="text-2xl font-semibold text-primary hover:underline">{item.title}</Link>
              {company ? (
                company.slug ? (
                  <Link to={`/company/${company.slug}`} className="mt-1 block font-medium text-accent hover:underline">{company.name}</Link>
                ) : company.externalUrl ? (
                  // No internal company page for an external job — next best thing is wherever
                  // the ATS scrape actually points (its own site/LinkedIn), not a dead link.
                  <a href={company.externalUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-medium text-accent hover:underline">
                    {company.name} <ExternalLink className="size-3.5" />
                  </a>
                ) : (
                  <p className="mt-1 font-medium text-[#555]">{company.name}</p>
                )
              ) : null}
              {locationText ? <p className="mt-1 text-sm text-muted-foreground">{locationText}</p> : null}
            </div>
          </div>

          <div className="mt-6 border-b border-border">
            <span className="inline-flex border-b-2 border-accent px-1 pb-3 text-sm font-semibold text-primary">Job Detail</span>
          </div>

          {detail?.description ? (
            <div className="mt-5 max-w-2xl leading-7 text-[#555]" dangerouslySetInnerHTML={{ __html: sanitizeJobDescription(detail.description) }} />
          ) : <p className="mt-5 text-sm text-muted-foreground">Loading job details…</p>}
        </div>

        <div className="w-full shrink-0 lg:w-64">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={onApply} disabled={applied} className="flex-1">{applied ? 'Applied' : 'Apply'}</Button>
            <Button variant="outline" onClick={onSave} disabled={applied}><Bookmark className="size-4" /> {saved ? 'Remove saved' : 'Save'}</Button>
            <div className="relative" ref={menuRef}>
              <Button type="button" size="icon" variant="ghost" aria-label="More options" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
                <MoreHorizontal className="size-5" />
              </Button>
              {menuOpen ? (
                <div role="menu" className="absolute right-0 top-[calc(100%+8px)] z-10 w-44 rounded-xl border border-border bg-white p-1.5 shadow-lg">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/job/${item.slug}`); setMenuOpen(false) }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[#555] hover:bg-input"
                  >
                    <Copy className="size-4" /> Copy job link
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid gap-4">
            {seniorityLabels.length ? <InfoRow label="Seniority" value={seniorityLabels.join(', ')} /> : null}
            {typeLabel ? <InfoRow label="Type" value={typeLabel} /> : null}
            {industryLabels.length ? <InfoRow label="Industry" value={industryLabels.join(', ')} /> : null}
            {skillLabels.length ? (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Skills</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">{skillLabels.map((skill) => <Badge key={skill} variant="outline">{skill}</Badge>)}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-[#404040]">{value}</p>
    </div>
  )
}

interface CompanyView {
  name: string
  logoUrl?: string
  /** Internal `/company/:slug` — only ever set for a REAL `companies` row. */
  slug?: string
  /**
   * External jobs have no internal company page (see the class comment below), so the next best
   * "learn more about this company" destination is whatever URL the ATS scrape captured. Any one
   * of `socialLinks`' values does — its keys don't fully resolve against
   * `lookup_items.communication` (see `job-search.service.ts`'s comment), so this doesn't try to
   * prefer "the" LinkedIn one over the others, it just needs A real link. `applyUrl` is the
   * fallback under that: every external job carries one (measured: 3,271/3,271), but only 81% of
   * external jobs (2,662/3,271) have a non-empty `socialLinks` — this closes that gap, since a
   * job's own ATS listing page is still specific to that company, not a generic dead end.
   */
  externalUrl?: string
}

/**
 * `companyId` alone resolves a name/logo for the minority of jobs that are real recruiting-tenant
 * postings. The majority — external, ATS-scraped — carry a synthetic `companyId` with no matching
 * `companies` row at all (see `JobExternal`'s own comment: measured at 3,271/4,822 active jobs,
 * every sampled one dangling); `external.companyData` is where the scraper actually put the name.
 * A caller resolving only via `companyId` shows a blank name on most of the feed. External jobs
 * get no `slug` here — there is no company detail page for a company that isn't a real row —
 * `externalUrl` is the fallback destination for those instead.
 */
function resolveCompany(item: JobSearchItem, companiesById: Record<string, CompanyItem>): CompanyView {
  const real = companiesById[item.companyId]
  if (real) {
    const logo = companyLogoUrl(real.logoUrl)
    return { name: real.name, ...(logo ? { logoUrl: logo } : {}), ...(real.slug ? { slug: real.slug } : {}) }
  }
  const external = item.external?.companyData
  const logo = companyLogoUrl(external?.logoUrl)
  const externalUrl = Object.values(external?.socialLinks ?? {})[0] ?? item.external?.applyUrl
  return { name: external?.name ?? '', ...(logo ? { logoUrl: logo } : {}), ...(externalUrl ? { externalUrl } : {}) }
}

function toJobRow(item: JobSearchItem, ctx: { companiesById: Record<string, CompanyItem>; citiesById: Record<number, CityItem>; workspaceTypes: MetadataItem[] }): JobRow {
  const company = resolveCompany(item, ctx.companiesById)
  const city = item.location?.cityId !== undefined ? ctx.citiesById[item.location.cityId] : undefined
  const workMode = ctx.workspaceTypes.find((w) => w.elId === item.workspaceType)?.value
  return {
    key: item._id,
    slug: item.slug,
    title: item.title,
    company: company.name,
    ...(company.logoUrl ? { companyLogoUrl: company.logoUrl } : {}),
    location: formatLocation(city, item.location?.country),
    badges: workMode ? [workMode] : [],
  }
}

function citiesToOptions(cities: CityItem[]): SearchPickerOption[] {
  return cities.map((c) => ({ id: String(c.cityId), label: formatLocation(c, c.country), data: c }))
}

function readFilters(params: URLSearchParams): Record<FilterKey, string[]> {
  return { cities: readList(params, 'cities'), jobTypes: readList(params, 'jobTypes'), industries: readList(params, 'industries'), degreeTypes: readList(params, 'degreeTypes'), degreeFields: readList(params, 'degreeFields'), seniority: readList(params, 'seniority') }
}

function readList(params: URLSearchParams, key: FilterKey) {
  return (params.get(key) ?? '').split('|').filter(Boolean)
}

function toOptions(items: MetadataItem[]): SelectOption[] {
  return items.map((item) => ({ value: String(item.elId), label: item.value })).sort((a, b) => a.label.localeCompare(b.label))
}

/** Real-mode filter values are elIds carried as URL strings — `undefined` (not `[]`) when empty, so `searchJobs` skips the param entirely rather than sending a `terms` filter that matches nothing. */
function toNumberList(values: string[]): number[] | undefined {
  const nums = values.map(Number).filter((n) => Number.isFinite(n))
  return nums.length ? nums : undefined
}
